import React from 'react';

interface AddChildCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  formLoading: boolean;
  formError: string | null;
  formSuccess: string | null;
  parentCompanyData?: {
    _id: string;
    companyName: string;
  } | null;
  // New prop to pass all companies for dropdown
  availableParentCompanies?: { _id: string; companyName: string }[];
  editingCompany?: {
    _id: string;
    companyName: string;
    address: string;
    parentCompanyId: string;
  } | null;
}

const AddChildCompanyModal: React.FC<AddChildCompanyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  formLoading,
  formError,
  formSuccess,
  parentCompanyData,
  availableParentCompanies = [],
  editingCompany
}) => {
  const [companyData, setCompanyData] = React.useState<{
    companyName: string;
    address: string;
    parentCompanyId: string;
  }>({
    companyName: '',
    address: '',
    parentCompanyId: ''
  });

  const [validationError, setValidationError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      if (editingCompany) {
        // If editing, populate with existing data
        // Handle case where parentCompanyId might be an object (populated) or string
        let pId = editingCompany.parentCompanyId || '';
        if (typeof pId === 'object' && pId !== null) {
          // @ts-ignore - Handle potential populated object from backend
          pId = pId._id || pId.id || '';
        }

        setCompanyData({
          companyName: editingCompany.companyName || '',
          address: editingCompany.address || '',
          parentCompanyId: typeof pId === 'string' ? pId : ''
        });
      } else {
        // If creating, handle parent company data
        if (parentCompanyData) {
          setCompanyData(prev => ({ ...prev, parentCompanyId: parentCompanyData._id }));
        } else {
          setCompanyData(prev => ({ ...prev, parentCompanyId: '' }));
        }
        // Reset other fields when modal opens for creation
        setCompanyData(prev => ({
          ...prev,
          companyName: '',
          address: ''
        }));
      }
      setValidationError(null);
    }
  }, [parentCompanyData, editingCompany, isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: value
    }));
    setValidationError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validation
    if (!companyData.parentCompanyId && !parentCompanyData) {
      setValidationError('Please select a Parent Company');
      return;
    }
    if (!companyData.companyName.trim()) {
      setValidationError('Company Name is required');
      return;
    }
    // ... other validations

    onSubmit(companyData);
  };

  const handleClose = () => {
    setCompanyData({
      companyName: '',
      address: '',
      parentCompanyId: ''
    });
    onClose();
  };

  return (
    <div
      className="modal fade show"
      tabIndex={-1}
      style={{
        display: 'block',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1060,
      }}
      onClick={handleClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        style={{
          maxWidth: '600px',
          margin: '1.75rem auto',
          minHeight: 'calc(100% - 3.5rem)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0 shadow-lg" style={{ maxHeight: '100%' }}>
          <div className="modal-header py-3" style={{
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            color: 'white',
            borderRadius: '0.3rem 0.3rem 0 0'
          }}>
            <h5 className="modal-title fw-bold">
              <i className="bi bi-building me-2"></i>
              {editingCompany ? 'Edit Child Company' : 'Add Child Company'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              disabled={formLoading}
            ></button>
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="modal-body p-4" style={{ overflowY: 'auto' }}>
              {formError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {formError}
                </div>
              )}
              {formSuccess && (
                <div className="alert alert-success d-flex align-items-center mb-4 border-0 bg-success bg-opacity-10 text-success">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  <div>{formSuccess}</div>
                </div>
              )}

              {validationError && (
                <div className="alert alert-danger d-flex align-items-center mb-4 border-0 bg-danger bg-opacity-10 text-danger">
                  <i className="bi bi-exclamation-circle-fill me-2"></i>
                  <div>{validationError}</div>
                </div>
              )}

              {/* Select Parent Company - Only show if not pre-selected */}
              {!parentCompanyData && (
                <div className="mb-4 text-start">
                  <label className="form-label fw-medium text-start d-block">
                    <span className="text-danger me-1">*</span>PARENT COMPANY
                  </label>
                  <select
                    className="form-select border-0 bg-light py-2 px-3"
                    name="parentCompanyId"
                    value={companyData.parentCompanyId}
                    onChange={handleInputChange}
                    style={{ borderRadius: '8px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)' }}
                    required
                  >
                    <option value="">Select Parent Company</option>
                    {availableParentCompanies.map(pc => (
                      <option key={pc._id} value={pc._id}>{pc.companyName}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-4 text-start">
                <label className="form-label fw-medium text-start d-block">
                  <span className="text-danger me-1">*</span>COMPANY NAME
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="companyName"
                  value={companyData.companyName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter child company name"
                />
                {parentCompanyData && (
                  <small className="form-text text-muted">
                    Parent company: {parentCompanyData.companyName}
                  </small>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label fw-medium text-start d-block">Address</label>
                <textarea
                  className="form-control"
                  name="address"
                  value={companyData.address}
                  onChange={handleInputChange}
                  placeholder="Enter company address"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer py-3 bg-light">
              <button
                type="button"
                className="btn btn-outline-primary px-4 py-2"
                onClick={handleClose}
                disabled={formLoading}
                style={{
                  color: '#1e3c72',
                  borderColor: '#1e3c72',
                }}
                onMouseEnter={(e) => {
                  if (!formLoading) {
                    e.currentTarget.style.backgroundColor = '#1e3c72';
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#1e3c72';
                }}
              >
                <i className="bi bi-x-circle me-1"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary px-4 py-2"
                disabled={formLoading}
                style={{
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                  border: 'none'
                }}
              >
                {formLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {editingCompany ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    {editingCompany ? 'Update Child Company' : 'Create Child Company'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddChildCompanyModal;